import { useCallback, useEffect, useRef, useState } from 'react';

import { push } from 'connected-react-router';
import { Form } from 'formik';
import isEmpty from 'lodash/isEmpty';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import Button from 'src/app/components/Elements/Button';
import SubmitButton from 'src/app/components/Elements/Button/Submit';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import { Divider } from 'src/app/components/Elements/Divider';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import Spinner from 'src/app/components/Elements/Spinner';
import { ToolTip } from 'src/app/components/Elements/ToolTip';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import { AddressAutoComplete } from 'src/app/components/Form/ApiAutoComplete/DeliveryAddress/container';
import CheckBoxInput from 'src/app/components/Form/CheckBoxInput';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import TextInput from 'src/app/components/Form/TextInput';
import ZipCodeInput from 'src/app/components/Form/TextInput/ZipCode/container';
import { UndeliverableAddressError } from 'src/app/components/Form/TextInput/ZipCode/UndeliverableAddressError';
import IdVerificationRequired from 'src/app/components/Pages/Checkout/IdVerificationRequired/container';
import * as paths from 'src/app/router/paths';
import { closeModal, deleteAccountAddress, openModal, segmentTrack } from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';
import { theme } from 'src/styles/theme';

import styles from './styles.module.scss';

const DeliveryAddressCreate = ({
  isValid,
  isSubmitting,
  hideLeaveWithDoorman,
  shouldEditAddress,
  initialAddress,
  errors,
  isValidating,
  cartUUID,
  accountId,
  handleSubmit,
  bffCartDeliveryAddressId,
  cartId,
  bffCart,
  deliveryAddresses,
  dispatchLoadAccount,
  dispatchLoadCart,
  accountIsFetching,
  bffCartIsFetching,
}) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const dispatchOpenModal = ({ modalType, handleConfirmClick, confirmMessage }) =>
    dispatch(openModal({ modalType, handleConfirmClick, confirmMessage }));
  const dispatchCloseModal = () => dispatch(closeModal());
  const dispatchSegmentTrack = useCallback(
    (event: UnifiedEvent, payload: any) => dispatch(segmentTrack(event, payload) as any),
    [dispatch]
  );
  const dispatchAccountDeleteAddress = (addressId, accountId) => dispatch(deleteAccountAddress(addressId, accountId));
  const [isDeleteSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEmpty(initialAddress)) {
      dispatchSegmentTrack(UNIFIED.viewAddAddress, { source: 'Checkout' });
    } else {
      dispatchSegmentTrack(UNIFIED.viewEditExistingAddress, { source: 'Checkout' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!accountIsFetching && isEmpty(deliveryAddresses)) {
      dispatchLoadAccount();
    }

    if (!bffCartIsFetching && !bffCart) {
      dispatchLoadCart(cartId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteAddress = async ({ addressId, accountId }) => {
    setIsSubmitting(true);
    dispatchCloseModal();
    await dispatchAccountDeleteAddress(addressId, accountId);
    dispatchSegmentTrack(UNIFIED.deleteDeliveryAddress, { location: 'Address Form Checkout' });
    setIsSubmitting(false);
    dispatch(push(paths.checkoutSelectAddress.toPath({ cartUUID })));
  };

  const handleDelete = () => {
    dispatchOpenModal({
      modalType: 'confirm',
      handleConfirmClick: () => handleDeleteAddress({ addressId: initialAddress?.id, accountId: accountId }),
      confirmMessage: 'Are you sure you want to delete this address?',
    });
  };
  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false);

  const title = isEmpty(initialAddress) ? 'Add a delivery address' : 'Edit delivery address';
  if (accountIsFetching || bffCartIsFetching)
    return (
      <StandardPage title={title}>
        <Spinner centered />
      </StandardPage>
    );

  return (
    <StandardPage title={title}>
      <div>
        <div style={{ marginBottom: '2rem' }}>
          {isEmpty(initialAddress) && (
            <div className="normal">
              Capsule delivers your medications to your home, work, a friend’s house — anywhere.
            </div>
          )}
        </div>
        <IdVerificationRequired />
        <Form ref={formRef}>
          <SegmentFormIncomplete />
          <Card>
            <CardItem>
              <UndeliverableAddressError />

              <AddressAutoComplete
                textInputProps={{
                  name: 'street',
                  id: 'street',
                  type: 'text',
                  label: 'Street address*',
                  'data-cy': 'street',
                  helperText: '*Required',
                }}
                relatedFields={{ zipCode: 'zip_code' }}
              />
              <TextInput name="street_two" label="Apt, suite, floor, etc." data-cy="street_two" />
              <ZipCodeInput name="zip_code" label="Zip code*" helperText="*Required" data_cy="zip_code" compactStyle />
              <TextInput
                name="notes"
                label="Delivery instructions"
                data-cy="note"
                helperText="For example: access code, call from lobby, etc."
              />
              {!hideLeaveWithDoorman && (
                <CheckBoxInput hasBlueSentenceLabel name="is_doorman" label="Leave with doorman" data-cy="doorman" />
              )}
            </CardItem>
          </Card>
          <div className="mt-4 d-flex justify-content-center">
            <ToolTip
              anchorText="Someone must be available to receive every order. We don’t leave medications unattended."
              header="Every Capsule order requires someone present."
              body="For every order there needs to be someone – a friend, a doorman, a colleague – to receive and sign for your medication when it arrives."
              analyticTip="Signature requirement"
            />
          </div>
          <SubmitButton
            className={styles.submitButton}
            size="medium"
            style="newCheckout"
            block
            disabled={shouldEditAddress ? false : !isValid}
            isSubmitting={isSubmitting}
            onClick={handleSubmit}
            data-cy="checkout-save-address"
          >
            Save
          </SubmitButton>
          {!isEmpty(initialAddress) && bffCartDeliveryAddressId !== initialAddress.id && (
            <>
              <Divider margin={theme.dividerMargins.lg} />
              <Button
                className={styles.submitButton}
                size="medium"
                style="secondary"
                block
                isSubmitting={isDeleteSubmitting}
                onClick={handleDelete}
                data-cy="checkout-delete-address"
                type="button"
              >
                Delete Address
              </Button>
            </>
          )}
        </Form>
      </div>
    </StandardPage>
  );
};

export default DeliveryAddressCreate;
